
import React, { ReactNode } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Code2, Palette, Layout, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ComponentDocProps {
  name: string;
  description: string;
  preview: ReactNode;
  propsList?: PropDefinition[];
  code?: string;
  className?: string;
  designNotes?: string;
  variants?: {
    title: string;
    preview: ReactNode;
  }[];
}

export interface PropDefinition {
  name: string;
  type: string;
  default?: string;
  required?: boolean;
  description: string;
}

export function ComponentDoc({
  name,
  description,
  preview,
  propsList,
  code,
  className,
  designNotes,
  variants,
}: ComponentDocProps) {
  return (
    <div className={cn("not-prose", className)}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layout className="h-5 w-5 text-primary" />
            <span>{name}</span>
          </CardTitle>
        </CardHeader>
        
        <Tabs defaultValue="preview" className="px-6">
          <TabsList className="mb-4">
            <TabsTrigger value="preview">Preview</TabsTrigger>
            {propsList && <TabsTrigger value="props">Props</TabsTrigger>}
            {designNotes && <TabsTrigger value="design">Design</TabsTrigger>}
            {code && <TabsTrigger value="code">Code</TabsTrigger>}
            {variants && <TabsTrigger value="variants">Variants</TabsTrigger>}
          </TabsList>
          
          <TabsContent value="preview" className="space-y-6">
            <p className="text-sm text-muted-foreground">{description}</p>
            
            <div className="flex justify-center rounded-md border p-6">
              {preview}
            </div>
          </TabsContent>
          
          {propsList && (
            <TabsContent value="props">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted text-muted-foreground">
                    <tr>
                      <th className="p-2 font-medium">Prop</th>
                      <th className="p-2 font-medium">Type</th>
                      <th className="p-2 font-medium">Default</th>
                      <th className="p-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propsList.map((prop) => (
                      <tr key={prop.name} className="border-b">
                        <td className="p-2 font-mono text-xs">
                          {prop.name}
                          {prop.required && <span className="text-red-500">*</span>}
                        </td>
                        <td className="p-2 font-mono text-xs">{prop.type}</td>
                        <td className="p-2 font-mono text-xs">
                          {prop.default ? prop.default : "-"}
                        </td>
                        <td className="p-2">{prop.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
          
          {designNotes && (
            <TabsContent value="design">
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <Palette className="h-5 w-5 mt-0.5 text-primary" />
                  <h3 className="font-medium">Design Notes</h3>
                </div>
                <div className="prose prose-sm max-w-none">
                  <p>{designNotes}</p>
                </div>
              </div>
            </TabsContent>
          )}
          
          {code && (
            <TabsContent value="code">
              <div className="flex items-start gap-2 mb-2">
                <Code2 className="h-5 w-5 mt-0.5 text-primary" />
                <h3 className="font-medium">Example Code</h3>
              </div>
              <pre className="rounded-md bg-muted p-4 overflow-x-auto">
                <code className="text-sm">{code}</code>
              </pre>
            </TabsContent>
          )}
          
          {variants && (
            <TabsContent value="variants">
              <div className="flex items-start gap-2 mb-4">
                <LayoutGrid className="h-5 w-5 mt-0.5 text-primary" />
                <h3 className="font-medium">Component Variants</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {variants.map((variant, i) => (
                  <div key={i} className="border rounded-md p-4">
                    <h4 className="text-sm font-medium mb-3">{variant.title}</h4>
                    <div className="flex justify-center p-4 bg-muted/30 rounded">
                      {variant.preview}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
        
        <CardFooter className="border-t bg-muted/20">
          <p className="text-xs text-muted-foreground">
            Component documentation for Grandpal design system
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
